import { useMemo, useState } from 'react';
import {
  LayoutChangeEvent,
  ScrollView,
  ScrollViewProps,
  StyleProp,
  ViewStyle,
} from 'react-native';
import { usePersistFn } from '../functions/hooks/usePersistFn';
import { FCC } from '../utils/react';

export interface CentralizedScrollViewProps {
  style?: StyleProp<ViewStyle>;
  horizontal?: boolean;
  contentContainerStyle?: StyleProp<ViewStyle>;
  scrollEnabled?: boolean;
  renderScrollView?: (props: ScrollViewProps) => JSX.Element;
}

/**
 * A ScrollView variant that centers the content in the main dimension when the
 * content is smaller than the ScrollView viewport, once the content is larger,
 * the layout turn back to the normal style.
 */
export const CentralizedScrollView: FCC<CentralizedScrollViewProps> = props => {
  const [scrollViewSize, setScrollViewSize] = useState({ width: 0, height: 0 });
  const [contentSize, setContentSize] = useState({ width: 0, height: 0 });

  const contentContainerStyle: StyleProp<ViewStyle> = useMemo(() => {
    const isContentSizeOverflow = props.horizontal
      ? contentSize.width > scrollViewSize.width
      : contentSize.height > scrollViewSize.height;

    // It is necessary not to abbreviate to `marginVertical`/`marginHorizontal`,
    // the abbreviations will not center the content, but this way can
    const centralizeMainAxisStyle = props.horizontal
      ? { marginLeft: 'auto', marginRight: 'auto' }
      : { marginTop: 'auto', marginBottom: 'auto' };

    return [
      !isContentSizeOverflow && centralizeMainAxisStyle,
      props.contentContainerStyle,
    ];
  }, [
    contentSize,
    scrollViewSize,
    props.contentContainerStyle,
    props.horizontal,
  ]);

  const onLayout = usePersistFn((event: LayoutChangeEvent) => {
    setScrollViewSize(returnNewSizeObjectIfChanged(event.nativeEvent.layout));
  });

  const onContentSizeChange = usePersistFn((width: number, height: number) => {
    setContentSize(returnNewSizeObjectIfChanged({ width, height }));
  });

  const scrollViewProps: ScrollViewProps = {
    style: props.style,
    horizontal: props.horizontal,
    onLayout,
    onContentSizeChange,
    contentContainerStyle,
    children: props.children,
    scrollEnabled: props.scrollEnabled,
  };

  const defaultRenderScrollView = (props: ScrollViewProps) => (
    <ScrollView {...props} />
  );

  return (props.renderScrollView ?? defaultRenderScrollView)(scrollViewProps);
};

interface SizeObject {
  width: number;
  height: number;
}
const returnNewSizeObjectIfChanged = (
  newObj: SizeObject,
): ((oldObj: SizeObject) => SizeObject) => {
  return oldObj => {
    return oldObj.width !== newObj.width || oldObj.height !== newObj.height
      ? newObj
      : oldObj;
  };
};
